import React, { useState, useCallback } from 'react'
import { type NurseryItemData } from '../nureseries/type'
import { getFilterLabel, nurseryNameWithPrefecture } from '../utils'
import { ImageCarousel } from './ImageCarousel'
import { CheckInMonth } from './CheckInMonth'
import { useSearchStore } from '../../../store/jpeTopPageStore'

interface NurseryFeaturesProps {
  features: string[]
}

export const NurseryFeatures: React.FC<NurseryFeaturesProps> = ({ features }) => {
  if (!features || features.length === 0) return null

  const featureLabels = features.map((feature) => getFilterLabel('nurseryFeatures', feature)).filter(Boolean)
  if (featureLabels.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2 my-3 mb-3">
      {featureLabels.map((feature, index) => (
        <span key={`feature-${index}`} className="px-3 py-1 text-xs font-medium rounded-sm bg-black text-white">
          {feature}
        </span>
      ))}
    </div>
  )
}

interface NurseryItemProps {
  nursery: NurseryItemData
  isSelected?: boolean
}

export const NurseryItem: React.FC<NurseryItemProps> = ({ nursery, isSelected = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const { checkInMonthsData, checkInMonthsLoading, checkInMonthsError, retryCheckInMonths } = useSearchStore()

  // Get checkInMonths data from store (or fall back to nursery data if available)
  const checkInMonths = checkInMonthsData[nursery.kintoneId] || nursery.checkInMonths || []
  const isLoading = checkInMonthsLoading.has(nursery.kintoneId)
  const hasError = checkInMonthsError.has(nursery.kintoneId)

  const handleIndexChange = (index: number) => {
    setCurrentImageIndex(index)
  }

  const handleCardClick = () => {
    if (!nursery.lpUrl) {
      return
    }

    const searchParams = new URLSearchParams(window.location.search)
    // Replace parameter names
    if (searchParams.has('maximum_number_of_adults')) {
      const value = searchParams.get('maximum_number_of_adults')
      searchParams.delete('maximum_number_of_adults')
      searchParams.set('number_of_adults', value)
    }

    if (searchParams.has('maximum_number_of_children')) {
      const value = searchParams.get('maximum_number_of_children')
      searchParams.delete('maximum_number_of_children')
      searchParams.set('number_of_study_abroad', value)
    }

    const modifiedSearch = searchParams.toString() ? '?' + searchParams.toString() : ''
    window.open(nursery.lpUrl + modifiedSearch, '_blank')
  }

  const handleRetry = useCallback(() => {
    retryCheckInMonths([nursery.kintoneId])
  }, [nursery.kintoneId, retryCheckInMonths])

  return (
    <div
      className={`overflow-hidden cursor-pointer transition-all ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
      onClick={handleCardClick}
    >
      <ImageCarousel
        images={nursery.keyVisualImages}
        badge={nursery.badge}
        currentIndex={currentImageIndex}
        onIndexChange={handleIndexChange}
      />

      <div className="pb-4">
        <NurseryFeatures features={nursery.feature} />

        <h3 className="text-lg text-gray-900 mb-2">{nurseryNameWithPrefecture(nursery)}</h3>

        <div className="flex items-start gap-2 mb-2">
          <svg width="19" height="18" viewBox="0 0 12 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5.83333 12.0625C7.20833 10.9514 8.24653 9.88194 8.94792 8.85417C9.64931 7.82639 10 6.86111 10 5.95833C10 4.70833 9.61111 3.68056 8.83333 2.875C8.05556 2.06944 7.05556 1.66667 5.83333 1.66667C4.61111 1.66667 3.61111 2.06944 2.83333 2.875C2.05556 3.68056 1.66667 4.70833 1.66667 5.95833C1.66667 6.86111 2.01736 7.82639 2.71875 8.85417C3.42014 9.88194 4.45833 10.9514 5.83333 12.0625ZM5.83333 14.1667C3.875 12.7222 2.41319 11.3194 1.44792 9.95833C0.482639 8.59722 0 7.26389 0 5.95833C0 4.22222 0.541667 2.79514 1.625 1.67708C2.70833 0.559028 4.11111 0 5.83333 0C7.55555 0 8.95833 0.559028 10.0417 1.67708C11.125 2.79514 11.6667 4.22222 11.6667 5.95833C11.6667 7.26389 11.184 8.59722 10.2187 9.95833C9.25347 11.3194 7.79167 12.7222 5.83333 14.1667ZM5.83333 7.5C6.29167 7.5 6.68403 7.33681 7.01042 7.01042C7.33681 6.68403 7.5 6.29167 7.5 5.83333C7.5 5.375 7.33681 4.98264 7.01042 4.65625C6.68403 4.32986 6.29167 4.16667 5.83333 4.16667C5.375 4.16667 4.98264 4.32986 4.65625 4.65625C4.32986 4.98264 4.16667 5.375 4.16667 5.83333C4.16667 6.29167 4.32986 6.68403 4.65625 7.01042C4.98264 7.33681 5.375 7.5 5.83333 7.5ZM0 16.6667V15H11.6667V16.6667H0Z"
              fill="#8B9096"
            />
          </svg>
          <span className="text-sm text-gray-600">{nursery.location}</span>
        </div>

        <div className="flex flex-col gap-2 mb-4 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 15V13.6875C0 13.0903 0.305556 12.6042 0.916667 12.2292C1.52778 11.8542 2.33333 11.6667 3.33333 11.6667C3.51389 11.6667 3.6875 11.6701 3.85417 11.6771C4.02083 11.684 4.18056 11.7014 4.33333 11.7292C4.13889 12.0208 3.99306 12.3264 3.89583 12.6458C3.79861 12.9653 3.75 13.2986 3.75 13.6458V15H0ZM5 15V13.6458C5 13.2014 5.12153 12.7951 5.36458 12.4271C5.60764 12.059 5.95139 11.7361 6.39583 11.4583C6.84028 11.1806 7.37153 10.9722 7.98958 10.8333C8.60764 10.6944 9.27778 10.625 10 10.625C10.7361 10.625 11.4132 10.6944 12.0312 10.8333C12.6493 10.9722 13.1806 11.1806 13.625 11.4583C14.0694 11.7361 14.4097 12.059 14.6458 12.4271C14.8819 12.7951 15 13.2014 15 13.6458V15H5ZM16.25 15V13.6458C16.25 13.2847 16.2049 12.9444 16.1146 12.625C16.0243 12.3056 15.8889 12.0069 15.7083 11.7292C15.8611 11.7014 16.0174 11.684 16.1771 11.6771C16.3368 11.6701 16.5 11.6667 16.6667 11.6667C17.6667 11.6667 18.4722 11.8507 19.0833 12.2188C19.6944 12.5868 20 13.0764 20 13.6875V15H16.25ZM6.77083 13.3333H13.25C13.1111 13.0556 12.7257 12.8125 12.0938 12.6042C11.4618 12.3958 10.7639 12.2917 10 12.2917C9.23611 12.2917 8.53819 12.3958 7.90625 12.6042C7.27431 12.8125 6.89583 13.0556 6.77083 13.3333ZM3.33333 10.8333C2.875 10.8333 2.48264 10.6701 2.15625 10.3438C1.82986 10.0174 1.66667 9.625 1.66667 9.16667C1.66667 8.69444 1.82986 8.29861 2.15625 7.97917C2.48264 7.65972 2.875 7.5 3.33333 7.5C3.80556 7.5 4.20139 7.65972 4.52083 7.97917C4.84028 8.29861 5 8.69444 5 9.16667C5 9.625 4.84028 10.0174 4.52083 10.3438C4.20139 10.6701 3.80556 10.8333 3.33333 10.8333ZM16.6667 10.8333C16.2083 10.8333 15.816 10.6701 15.4896 10.3438C15.1632 10.0174 15 9.625 15 9.16667C15 8.69444 15.1632 8.29861 15.4896 7.97917C15.816 7.65972 16.2083 7.5 16.6667 7.5C17.1389 7.5 17.5347 7.65972 17.8542 7.97917C18.1736 8.29861 18.3333 8.69444 18.3333 9.16667C18.3333 9.625 18.1736 10.0174 17.8542 10.3438C17.5347 10.6701 17.1389 10.8333 16.6667 10.8333ZM10 10C9.30556 10 8.71528 9.75694 8.22917 9.27083C7.74306 8.78472 7.5 8.19444 7.5 7.5C7.5 6.79167 7.74306 6.19792 8.22917 5.71875C8.71528 5.23958 9.30556 5 10 5C10.7083 5 11.3021 5.23958 11.7812 5.71875C12.2604 6.19792 12.5 6.79167 12.5 7.5C12.5 8.19444 12.2604 8.78472 11.7812 9.27083C11.3021 9.75694 10.7083 10 10 10ZM10 8.33333C10.2361 8.33333 10.434 8.25347 10.5938 8.09375C10.7535 7.93403 10.8333 7.73611 10.8333 7.5C10.8333 7.26389 10.7535 7.06597 10.5938 6.90625C10.434 6.74653 10.2361 6.66667 10 6.66667C9.76389 6.66667 9.56597 6.74653 9.40625 6.90625C9.24653 7.06597 9.16667 7.26389 9.16667 7.5C9.16667 7.73611 9.24653 7.93403 9.40625 8.09375C9.56597 8.25347 9.76389 8.33333 10 8.33333Z"
                fill="#8B9096"
              />
            </svg>
            <span>{nursery.acceptClass} Class</span>
          </div>
          <div className="flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.0007 18.3337C7.84787 18.3337 5.97982 17.6184 4.39648 16.1878C2.81315 14.7573 1.91732 12.9725 1.70898 10.8337H3.39648C3.60482 12.5142 4.33746 13.9066 5.5944 15.0107C6.85135 16.1149 8.3201 16.667 10.0007 16.667C11.8618 16.667 13.4382 16.0212 14.7298 14.7295C16.0215 13.4378 16.6673 11.8614 16.6673 10.0003C16.6673 8.13921 16.0215 6.56283 14.7298 5.27116C13.4382 3.97949 11.8618 3.33366 10.0007 3.33366C8.80621 3.33366 7.69857 3.6288 6.67773 4.21908C5.6569 4.80935 4.84787 5.62533 4.25065 6.66699H6.66732V8.33366H1.83398C2.23676 6.38921 3.20204 4.79199 4.72982 3.54199C6.2576 2.29199 8.01454 1.66699 10.0007 1.66699C11.1534 1.66699 12.2368 1.88574 13.2507 2.32324C14.2645 2.76074 15.1465 3.35449 15.8965 4.10449C16.6465 4.85449 17.2402 5.73644 17.6777 6.75033C18.1152 7.76421 18.334 8.84755 18.334 10.0003C18.334 11.1531 18.1152 12.2364 17.6777 13.2503C17.2402 14.2642 16.6465 15.1462 15.8965 15.8962C15.1465 16.6462 14.2645 17.2399 13.2507 17.6774C12.2368 18.1149 11.1534 18.3337 10.0007 18.3337ZM12.334 13.5003L9.16732 10.3337V5.83366H10.834V9.66699L13.5007 12.3337L12.334 13.5003Z"
                fill="#8B9096"
              />
            </svg>
            <span>Program Hours | {nursery.programHours}</span>
          </div>
          {nursery.reviewCount > 0 && (
            <div className="flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.5638 11.8753L10.0013 10.3962L12.4388 11.8753L11.793 9.10449L13.9596 7.22949L11.1055 7.00033L10.0013 4.37533L8.89713 7.00033L6.04297 7.22949L8.20964 9.10449L7.5638 11.8753ZM1.66797 18.3337V3.33366C1.66797 2.87533 1.83116 2.48296 2.15755 2.15658C2.48394 1.83019 2.8763 1.66699 3.33464 1.66699H16.668C17.1263 1.66699 17.5187 1.83019 17.8451 2.15658C18.1714 2.48296 18.3346 2.87533 18.3346 3.33366V13.3337C18.3346 13.792 18.1714 14.1844 17.8451 14.5107C17.5187 14.8371 17.1263 15.0003 16.668 15.0003H5.0013L1.66797 18.3337ZM4.29297 13.3337H16.668V3.33366H3.33464V14.2712L4.29297 13.3337Z"
                  fill="#8B9096"
                />
              </svg>
              <span>{nursery.reviewCount} reviews</span>
            </div>
          )}
        </div>

        {nursery.highlights.length > 0 && (
          <div
            className="mb-4 p-3 rounded-lg"
            style={{
              backgroundColor: '#FFF7F7',
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9.16797 17.5V15.8333H15.8346V9.91667C15.8346 8.29167 15.2687 6.91319 14.1367 5.78125C13.0048 4.64931 11.6263 4.08333 10.0013 4.08333C8.3763 4.08333 6.99783 4.64931 5.86588 5.78125C4.73394 6.91319 4.16797 8.29167 4.16797 9.91667V15H3.33464C2.8763 15 2.48394 14.8368 2.15755 14.5104C1.83116 14.184 1.66797 13.7917 1.66797 13.3333V11.6667C1.66797 11.375 1.74089 11.1007 1.88672 10.8438C2.03255 10.5868 2.23741 10.3819 2.5013 10.2292L2.5638 9.125C2.67491 8.18056 2.94922 7.30556 3.38672 6.5C3.82422 5.69444 4.37283 4.99306 5.03255 4.39583C5.69227 3.79861 6.44922 3.33333 7.30338 3C8.15755 2.66667 9.05686 2.5 10.0013 2.5C10.9457 2.5 11.8416 2.66667 12.6888 3C13.536 3.33333 14.293 3.79514 14.9596 4.38542C15.6263 4.97569 16.1749 5.67361 16.6055 6.47917C17.036 7.28472 17.3138 8.15972 17.4388 9.10417L17.5013 10.1875C17.7652 10.3125 17.97 10.5 18.1159 10.75C18.2617 11 18.3346 11.2639 18.3346 11.5417V13.4583C18.3346 13.7361 18.2617 14 18.1159 14.25C17.97 14.5 17.7652 14.6875 17.5013 14.8125V15.8333C17.5013 16.2917 17.3381 16.684 17.0117 17.0104C16.6853 17.3368 16.293 17.5 15.8346 17.5H9.16797ZM7.5013 11.6667C7.26519 11.6667 7.06727 11.5868 6.90755 11.4271C6.74783 11.2674 6.66797 11.0694 6.66797 10.8333C6.66797 10.5972 6.74783 10.3993 6.90755 10.2396C7.06727 10.0799 7.26519 10 7.5013 10C7.73741 10 7.93533 10.0799 8.09505 10.2396C8.25477 10.3993 8.33463 10.5972 8.33463 10.8333C8.33463 11.0694 8.25477 11.2674 8.09505 11.4271C7.93533 11.5868 7.73741 11.6667 7.5013 11.6667ZM12.5013 11.6667C12.2652 11.6667 12.0673 11.5868 11.9076 11.4271C11.7478 11.2674 11.668 11.0694 11.668 10.8333C11.668 10.5972 11.7478 10.3993 11.9076 10.2396C12.0673 10.0799 12.2652 10 12.5013 10C12.7374 10 12.9353 10.0799 13.0951 10.2396C13.2548 10.3993 13.3346 10.5972 13.3346 10.8333C13.3346 11.0694 13.2548 11.2674 13.0951 11.4271C12.9353 11.5868 12.7374 11.6667 12.5013 11.6667ZM5.02214 10.375C4.92491 8.90278 5.36936 7.63889 6.35547 6.58333C7.34158 5.52778 8.57075 5 10.043 5C11.2791 5 12.3659 5.39236 13.3034 6.17708C14.2409 6.96181 14.8069 7.96528 15.0013 9.1875C13.7374 9.17361 12.5742 8.83333 11.5117 8.16667C10.4492 7.5 9.63325 6.59722 9.0638 5.45833C8.84158 6.56944 8.37283 7.55903 7.65755 8.42708C6.94227 9.29514 6.0638 9.94444 5.02214 10.375Z"
                  fill="#F05252"
                />
              </svg>
              <h4 className="text-sm font-semibold">Our Planner’s Favorite Points</h4>
            </div>

            <ul className="space-y-1">
              {nursery.highlights.map((highlight, index) => (
                <li key={`highlight-${index}`} className="text-sm text-gray-600 pl-4">
                  • {highlight}
                </li>
              ))}
            </ul>
          </div>
        )}
        <CheckInMonth
          checkInMonths={checkInMonths}
          nurseryId={nursery.kintoneId}
          availabilityUrl={nursery.availabilityUrl}
          loading={isLoading}
          error={hasError}
          onRetry={handleRetry}
        />
      </div>
    </div>
  )
}
